import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { toast } from "react-toastify";
import ReportMetricCard from "../Components/ReportMetricCard";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDefaultFilters = () => {
  const today = new Date();
  const from = new Date(today.getFullYear(), today.getMonth(), 1);
  return { fromDate: formatDate(from), toDate: formatDate(today), remark: "" };
};

const toCurrency = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const dayLabel = (value) => {
  if (!value) return "-";
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const monthLabel = (value) => {
  const [y, m] = String(value || "").split("-");
  if (!y || !m) return "-";
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short" });
};

const defaultExpense = () => ({
  expense_remark: "",
  amount: "",
  expense_Date: formatDate(new Date()),
});

function Expenses() {
  const defaults = useMemo(() => getDefaultFilters(), []);
  const [filterForm, setFilterForm] = useState(defaults);
  const [filters, setFilters] = useState(defaults);
  const [chartView, setChartView] = useState("day");
  const [chartMonth, setChartMonth] = useState(defaults.toDate.slice(0, 7));

  const [rows, setRows] = useState([]);
  const [tableSummary, setTableSummary] = useState({ total_records: 0, total_expense: 0 });
  const [summary, setSummary] = useState({
    totals: { net_income: 0, gross_income: 0, total_expense: 0, real_profit: 0 },
    day_wise: [],
    month_wise: [],
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [tableError, setTableError] = useState("");
  const [summaryError, setSummaryError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [modalOpen, setModalOpen] = useState(false);
  const [expenseForm, setExpenseForm] = useState(defaultExpense());
  const [saving, setSaving] = useState(false);

  const params = useMemo(() => {
    const p = {};
    if (filters.fromDate) p.fromDate = filters.fromDate;
    if (filters.toDate) p.toDate = filters.toDate;
    if (filters.remark) p.remark = filters.remark;
    return p;
  }, [filters]);

  const loadTable = useCallback(async () => {
    setTableLoading(true);
    setTableError("");
    try {
      const res = await axios.get("https://serverkf.ccsoftwares.com/expenses/getExpenses", { params });
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setTableSummary({
        total_records: Number(res.data?.summary?.total_records || 0),
        total_expense: Number(res.data?.summary?.total_expense || 0),
      });
    } catch (_e) {
      setRows([]);
      setTableSummary({ total_records: 0, total_expense: 0 });
      setTableError("Unable to load expenses.");
    } finally {
      setTableLoading(false);
    }
  }, [params]);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError("");
    try {
      const res = await axios.get("https://serverkf.ccsoftwares.com/expenses/getProfitSummary", { params });
      setSummary({
        totals: {
          net_income: Number(res.data?.totals?.net_income || 0),
          gross_income: Number(res.data?.totals?.gross_income || 0),
          total_expense: Number(res.data?.totals?.total_expense || 0),
          real_profit: Number(res.data?.totals?.real_profit || 0),
        },
        day_wise: Array.isArray(res.data?.day_wise) ? res.data.day_wise : [],
        month_wise: Array.isArray(res.data?.month_wise) ? res.data.month_wise : [],
      });
    } catch (_e) {
      setSummary({
        totals: { net_income: 0, gross_income: 0, total_expense: 0, real_profit: 0 },
        day_wise: [],
        month_wise: [],
      });
      setSummaryError("Unable to load summary.");
    } finally {
      setSummaryLoading(false);
    }
  }, [params]);

  useEffect(() => {
    loadTable();
    loadSummary();
  }, [loadTable, loadSummary]);

  const applyFilters = () => {
    if (filterForm.fromDate && filterForm.toDate && filterForm.fromDate > filterForm.toDate) {
      toast.error("From date cannot be greater than To date.");
      return;
    }
    setFilters({
      fromDate: filterForm.fromDate,
      toDate: filterForm.toDate,
      remark: filterForm.remark.trim(),
    });
    setPage(0);
    if (filterForm.toDate) setChartMonth(filterForm.toDate.slice(0, 7));
  };

  const resetFilters = () => {
    const next = getDefaultFilters();
    setFilterForm(next);
    setFilters(next);
    setChartMonth(next.toDate.slice(0, 7));
    setPage(0);
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get("https://serverkf.ccsoftwares.com/expenses/downloadExpensesCsv", {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `expenses-${formatDate(new Date())}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error("Failed to download CSV.");
    }
  };

  const saveExpense = async () => {
    const remark = expenseForm.expense_remark.trim();
    const amount = Number(expenseForm.amount);
    if (!remark) return toast.error("Expense remark is required.");
    if (!Number.isFinite(amount) || amount <= 0) return toast.error("Invalid amount.");
    if (!expenseForm.expense_Date) return toast.error("Expense date is required.");

    setSaving(true);
    try {
      await axios.post("https://serverkf.ccsoftwares.com/expenses/createExpense", {
        expense_remark: remark,
        amount,
        expense_Date: expenseForm.expense_Date,
      });
      toast.success("Expense added.");
      setModalOpen(false);
      await Promise.all([loadTable(), loadSummary()]);
    } catch (_e) {
      toast.error("Failed to add expense.");
    } finally {
      setSaving(false);
    }
  };

  const pagedRows = useMemo(() => {
    if (rowsPerPage === -1) return rows;
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  const dayRows = useMemo(
    () =>
      (summary.day_wise || [])
        .map((r) => ({
          summary_date: String(r.summary_date || "").slice(0, 10),
          net_income: Number(r.net_income || 0),
          gross_income: Number(r.gross_income || 0),
          total_expense: Number(r.total_expense || 0),
          real_profit: Number(r.real_profit || 0),
        }))
        .sort((a, b) => b.summary_date.localeCompare(a.summary_date)),
    [summary.day_wise]
  );

  const monthRows = useMemo(
    () =>
      (summary.month_wise || [])
        .map((r) => ({
          summary_month: String(r.summary_month || "").slice(0, 7),
          net_income: Number(r.net_income || 0),
          gross_income: Number(r.gross_income || 0),
          total_expense: Number(r.total_expense || 0),
          real_profit: Number(r.real_profit || 0),
        }))
        .sort((a, b) => b.summary_month.localeCompare(a.summary_month)),
    [summary.month_wise]
  );

  const chartRows = useMemo(() => {
    if (chartView === "month") {
      return monthRows.map((r) => ({ label: r.summary_month, ...r }));
    }
    return dayRows
      .filter((r) => (chartMonth ? r.summary_date.startsWith(chartMonth) : true))
      .map((r) => ({ label: r.summary_date, ...r }));
  }, [chartView, chartMonth, dayRows, monthRows]);

  return (
    <Box sx={{ minHeight: "100vh", pt: 10, pb: 4, px: { xs: 2, md: 3 }, bgcolor: "background.default" }}>
      <Typography variant="h4" fontWeight={800}>Expense Management</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>Filter, log, and analyze expenses against net income.</Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <ReportMetricCard
            label="Net Income"
            value={`Rs. ${toCurrency(summary.totals.net_income)}`}
            tooltip="Net income from unique invoice headers: net_total after discount and service charge. This value is used for real profit."
            paperSx={{ p: 2, borderRadius: 3, bgcolor: "var(--brand-primary)", color: "white" }}
            labelSx={{ color: "rgba(255,255,255,0.8)" }}
            valueSx={{ fontSize: "1.25rem", fontWeight: 800 }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <ReportMetricCard
            label="Gross Sales"
            value={`Rs. ${toCurrency(summary.totals.gross_income)}`}
            tooltip="Gross sales from unique invoice headers: gross_total before invoice-level discount and service charge. This is shown for reference only."
            paperSx={{ p: 2, borderRadius: 3, bgcolor: "var(--brand-primary)", color: "white" }}
            labelSx={{ color: "rgba(255,255,255,0.8)" }}
            valueSx={{ fontSize: "1.25rem", fontWeight: 800 }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <ReportMetricCard
            label="Total Expenses"
            value={`Rs. ${toCurrency(summary.totals.total_expense)}`}
            tooltip="Sum of expense amounts for the selected filters. This is an expense total, not a gross or net sales amount."
            paperSx={{ p: 2, borderRadius: 3, bgcolor: "var(--brand-primary)", color: "white" }}
            labelSx={{ color: "rgba(255,255,255,0.8)" }}
            valueSx={{ fontSize: "1.25rem", fontWeight: 800 }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <ReportMetricCard
            label="Real Profit"
            value={`Rs. ${toCurrency(summary.totals.real_profit)}`}
            tooltip="Real profit is Net Income minus Total Expenses. Net Income uses invoice header net_total after discount and service charge."
            paperSx={{ p: 2, borderRadius: 3, bgcolor: "var(--brand-primary)", color: "white" }}
            labelSx={{ color: "rgba(255,255,255,0.8)" }}
            valueSx={{ fontSize: "1.25rem", fontWeight: 800 }}
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, border: "1px solid #E8CDCC" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}><TextField fullWidth label="From Date" type="date" InputLabelProps={{ shrink: true }} value={filterForm.fromDate} onChange={(e) => setFilterForm((p) => ({ ...p, fromDate: e.target.value }))} /></Grid>
          <Grid item xs={12} md={3}><TextField fullWidth label="To Date" type="date" InputLabelProps={{ shrink: true }} value={filterForm.toDate} onChange={(e) => setFilterForm((p) => ({ ...p, toDate: e.target.value }))} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="Search Expense Remark" value={filterForm.remark} onChange={(e) => setFilterForm((p) => ({ ...p, remark: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && applyFilters()} /></Grid>
          <Grid item xs={12} md={2}><Stack direction="row" spacing={1}><Button fullWidth variant="contained" startIcon={<FilterAltIcon />} onClick={applyFilters}>Apply</Button><Button fullWidth variant="outlined" startIcon={<RefreshIcon />} onClick={resetFilters}>Reset</Button></Stack></Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, border: "1px solid #E8CDCC" }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>Expense Table</Typography>
            <Typography variant="body2" color="text.secondary">
              {tableSummary.total_records} rows, Rs. {toCurrency(tableSummary.total_expense)} filtered expenses
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadCsv} disabled={!rows.length}>Download CSV</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setExpenseForm(defaultExpense()); setModalOpen(true); }}>Add New Expense</Button>
          </Stack>
        </Stack>
        {tableLoading ? <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box> : tableError ? <Typography color="error">{tableError}</Typography> : (
          <>
            <TableContainer>
              <Table>
                <TableHead><TableRow sx={{ bgcolor: "var(--brand-primary)" }}><TableCell sx={{ color: "white", fontWeight: 700 }}>ID</TableCell><TableCell sx={{ color: "white", fontWeight: 700 }}>Expense Date</TableCell><TableCell sx={{ color: "white", fontWeight: 700 }}>Remark</TableCell><TableCell align="right" sx={{ color: "white", fontWeight: 700 }}>Amount (Rs.)</TableCell></TableRow></TableHead>
                <TableBody>
                  {pagedRows.length === 0 ? <TableRow><TableCell colSpan={4} align="center">No records</TableCell></TableRow> : pagedRows.map((r) => (
                    <TableRow key={`${r.id}-${r.expense_Date}`} hover>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{String(r.expense_Date || "").replace("T", " ").slice(0, 19)}</TableCell>
                      <TableCell>{r.expense_remark}</TableCell>
                      <TableCell align="right">{toCurrency(r.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={rows.length} page={page} onPageChange={(_e, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, { label: "All", value: -1 }]} />
          </>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, border: "1px solid #E8CDCC" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={800}>Real Profit Analysis</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <ButtonGroup size="small">
              <Button variant={chartView === "day" ? "contained" : "outlined"} onClick={() => setChartView("day")}>Day-wise</Button>
              <Button variant={chartView === "month" ? "contained" : "outlined"} onClick={() => setChartView("month")}>Month-wise</Button>
            </ButtonGroup>
            {chartView === "day" ? <TextField size="small" type="month" label="Chart Month" InputLabelProps={{ shrink: true }} value={chartMonth} onChange={(e) => setChartMonth(e.target.value)} /> : null}
          </Stack>
        </Stack>

        {summaryLoading ? <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box> : summaryError ? <Typography color="error">{summaryError}</Typography> : chartRows.length === 0 ? <Typography color="text.secondary">No summary data.</Typography> : (
          <Box sx={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartRows}>
                <CartesianGrid strokeDasharray="4 4" />
                <XAxis dataKey="label" tickFormatter={(v) => (chartView === "day" ? dayLabel(v) : monthLabel(v))} />
                <YAxis tickFormatter={(v) => Math.round(Number(v || 0)).toLocaleString()} />
                <Tooltip labelFormatter={(v) => (chartView === "day" ? dayLabel(v) : monthLabel(v))} formatter={(v, n) => [`Rs. ${toCurrency(v)}`, n === "net_income" ? "Net Income" : n === "total_expense" ? "Expenses" : "Real Profit"]} />
                <Legend formatter={(v) => (v === "net_income" ? "Net Income" : v === "total_expense" ? "Expenses" : "Real Profit")} />
                <Bar dataKey="net_income" stackId="stack" fill="var(--brand-primary)" />
                <Bar dataKey="total_expense" stackId="stack" fill="#EBA6A3" stroke="var(--brand-primary)" />
                <Line type="monotone" dataKey="real_profit" stroke="#68100D" strokeDasharray="5 3" strokeWidth={3} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E8CDCC" }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>Day-wise Summary</Typography>
            <Table size="small"><TableHead><TableRow sx={{ bgcolor: "var(--brand-primary)" }}><TableCell>Date</TableCell><TableCell align="right">Net Income</TableCell><TableCell align="right">Expense</TableCell><TableCell align="right">Profit</TableCell></TableRow></TableHead><TableBody>{dayRows.slice(0, 10).map((r) => <TableRow key={r.summary_date}><TableCell>{dayLabel(r.summary_date)}</TableCell><TableCell align="right">{toCurrency(r.net_income)}</TableCell><TableCell align="right">{toCurrency(r.total_expense)}</TableCell><TableCell align="right" sx={{ color: r.real_profit >= 0 ? "#241B1B" : "var(--brand-primary)", fontWeight: 700 }}>{toCurrency(r.real_profit)}</TableCell></TableRow>)}</TableBody></Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E8CDCC" }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>Month-wise Summary</Typography>
            <Table size="small"><TableHead><TableRow sx={{ bgcolor: "var(--brand-primary)" }}><TableCell>Month</TableCell><TableCell align="right">Net Income</TableCell><TableCell align="right">Expense</TableCell><TableCell align="right">Profit</TableCell></TableRow></TableHead><TableBody>{monthRows.slice(0, 12).map((r) => <TableRow key={r.summary_month}><TableCell>{monthLabel(r.summary_month)}</TableCell><TableCell align="right">{toCurrency(r.net_income)}</TableCell><TableCell align="right">{toCurrency(r.total_expense)}</TableCell><TableCell align="right" sx={{ color: r.real_profit >= 0 ? "#241B1B" : "var(--brand-primary)", fontWeight: 700 }}>{toCurrency(r.real_profit)}</TableCell></TableRow>)}</TableBody></Table>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={modalOpen} onClose={() => !saving && setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Expense</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Expense Remark" multiline rows={3} fullWidth value={expenseForm.expense_remark} onChange={(e) => setExpenseForm((p) => ({ ...p, expense_remark: e.target.value }))} />
            <TextField label="Amount (Rs.)" type="number" inputProps={{ min: "0", step: "0.01" }} fullWidth value={expenseForm.amount} onChange={(e) => setExpenseForm((p) => ({ ...p, amount: e.target.value }))} />
            <TextField label="Expense Date" type="date" InputLabelProps={{ shrink: true }} fullWidth value={expenseForm.expense_Date} onChange={(e) => setExpenseForm((p) => ({ ...p, expense_Date: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveExpense} disabled={saving}>{saving ? "Saving..." : "Save Expense"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Expenses;
